import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  FlaskConical,
} from "lucide-react";
import { useClickOutside } from "../hooks/useClickOutside";

interface ToolSwitcherProps {
  isCollapsed?: boolean; // Optional prop for navbar collapse state
  isAdmin: boolean;
}

interface Tool {
  name: string;
  path: string;
  icon: React.ElementType | string; // Can be a Lucide icon component or an image path
}

const tools: Tool[] = [
  {
    name: "MetaRouter Architect",
    path: "/",
    icon: "/assets/architect.svg", // Changed to architect.svg
  },
  {
    name: "MetaRouter Labs",
    path: "/labs",
    icon: FlaskConical, // Changed to FlaskConical
  },
];

export default function ToolSwitcher({ isCollapsed = false, isAdmin }: ToolSwitcherProps) {
  const location = useLocation();
  const [isToolMenuOpen, setIsToolMenuOpen] = useState(false);
  const toolMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    () => {
      setIsToolMenuOpen(false);
    },
    [],
    [toolMenuRef]
  );

  const currentTool = tools.find(tool => location.pathname.startsWith(tool.path) && tool.path !== '/') || tools[0];

  if (!isAdmin) {
    return (
      <div className={`relative flex items-center ${isCollapsed ? 'flex-col pt-2' : 'gap-2 mr-2'}`} ref={toolMenuRef}>
        {typeof currentTool.icon === 'string' ? (
          <img src={currentTool.icon} alt={currentTool.name} className="h-5 w-5" />
        ) : (
          <currentTool.icon className="h-5 w-5" />
        )}
        {!isCollapsed && (
          <span className="font-medium text-white text-m ml-2">
            {currentTool.name}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`relative flex items-center ${isCollapsed ? 'flex-col' : 'gap-2'}`} ref={toolMenuRef}>
      <button
        onClick={() => setIsToolMenuOpen(!isToolMenuOpen)}
        className={`flex items-center p-0 group ${isCollapsed ? 'flex-col' : ''}`}
      >
        {typeof currentTool.icon === 'string' ? (
          <img src={currentTool.icon} alt={currentTool.name} className="h-5 w-5" />
        ) : (
          <currentTool.icon className="h-5 w-5" />
        )}
        {!isCollapsed && (
          <span className="font-medium text-white text-m ml-2">
            {currentTool.name}
          </span>
        )}
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isToolMenuOpen ? 'rotate-180' : ''} ${!isCollapsed ? 'ml-2' : 'mt-1'}`} />
      </button>

      {isToolMenuOpen && (
        <div
          className="fixed inset-0 bg-black transition-opacity duration-200 ease-in-out bg-opacity-0 z-40"
          onClick={() => setIsToolMenuOpen(false)}
        />
      )}
      <AnimatePresence>
        {isToolMenuOpen && (
          <motion.div
            className="absolute left-0 top-full mt-2 w-48 rounded-md shadow-lg bg-gray-900 border border-gray-800 dark:bg-gray-950 ring-1 ring-black ring-opacity-5 z-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="py-1" role="menu">
              {tools.map((tool) => (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="flex items-center px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                  role="menuitem"
                  onClick={() => setIsToolMenuOpen(false)}
                >
                  {typeof tool.icon === 'string' ? (
                    <img src={tool.icon} alt={tool.name} className="h-4 w-4 mr-2" />
                  ) : (
                    <tool.icon className="h-4 w-4 mr-2" />
                  )}
                  {tool.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
